import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import Session from '@/domains/shared/hooks/Session'
import { getApiErrorMessage } from '@/domains/shared/utils/getApiErrorMessage'
import { parseBlobErrorData } from '@/domains/shared/utils/parseBlobErrorData'
import type { User } from '@/types/User'

function extractAccessToken(data: unknown): string {
  const payload =
    data && typeof data === 'object' ? (data as Record<string, unknown>).data : undefined
  const p =
    payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : undefined
  if (!p || typeof p.accessToken !== 'string' || p.accessToken.length === 0) {
    throw new Error('Invalid refresh response: missing access token')
  }
  return p.accessToken
}

/** Whether a refreshed profile belongs to someone else than the one this tab holds. */
function isDifferentIdentity(previous: User | null, next: User | null): boolean {
  return (
    previous !== null &&
    next !== null &&
    (previous._id !== next._id || previous.companyId !== next.companyId)
  )
}

/**
 * A refresh failure is transient when the server throttled us (429), errored
 * (5xx), or the request never got a response (network). The refresh token is
 * still valid in those cases, so the session must not be destroyed - only a
 * definitive rejection (401/403, malformed response) means the session is dead.
 */
function isTransientRefreshError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false
  }
  const status = error.response?.status
  return status === undefined || status === 429 || status >= 500
}

/**
 * The API refused an action the stored profile may still claim: the caller's Role
 * was edited since the profile was read. The actions are re-read so the hidden and
 * shown controls catch up with what the server enforces.
 */
export function isPermissionRefusal(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 403) {
    return false
  }
  const data = error.response.data as { code?: unknown } | undefined
  return data?.code === 'INSUFFICIENT_PERMISSIONS'
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Run `fn` while holding a cross-tab lock, so only one tab refreshes at a time: the
 * server rotates the session on every refresh and treats a rotated value presented
 * again as theft. Without Web Locks (older browsers, some private windows) it runs
 * unlocked. See apps/web/CLAUDE.md, "Auth: sessions across tabs".
 */
export async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return fn()
  }
  return navigator.locks.request('inovio.auth.refresh', fn) as Promise<T>
}

/**
 * Singleton HTTP client.
 *
 * Access token is read per-request from the in-memory auth store.
 * On 401, the client silently calls POST /auth/refresh (the httpOnly session
 * cookie is sent automatically by the browser). If refresh succeeds the original
 * request is retried with the new access token. If refresh fails, the session is
 * cleared and the user is redirected to the sign-in page.
 */
class ApiClient {
  private static instance: ApiClient
  private readonly _apiClient
  private _isRefreshing = false
  private _refreshQueue: Array<{
    resolve: (token: string) => void
    reject: (error: Error) => void
  }> = []
  private _redirected = false
  /** The profile re-read in flight, so a burst of 403s reads it once. */
  private _profileSync: Promise<void> | null = null

  constructor() {
    this._apiClient = axios.create({
      baseURL: Session.getBaseURL(),
      withCredentials: true, // send httpOnly cookie on every request
    })

    this._apiClient.interceptors.request.use((request) => {
      if (!request.headers.Authorization) {
        const accessToken = Session.getToken()
        if (accessToken) {
          request.headers.Authorization = `Bearer ${accessToken}`
        }
      }
      return request
    })

    this._apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config
        await parseBlobErrorData(error)

        if (isPermissionRefusal(error) && !originalRequest?._skipAuthRetry) {
          void this.syncProfile()
        }

        if (
          error.response?.status !== 401 ||
          originalRequest._retried ||
          originalRequest._skipAuthRetry
        ) {
          const apiError = error instanceof Error ? error : new Error(String(error))
          apiError.message = getApiErrorMessage(apiError as AxiosError)
          return Promise.reject(apiError)
        }

        originalRequest._retried = true

        if (this._isRefreshing) {
          return new Promise<AxiosResponse>((resolve, reject) => {
            this._refreshQueue.push({
              resolve: (token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`
                resolve(this._apiClient(originalRequest))
              },
              reject,
            })
          })
        }

        this._isRefreshing = true
        try {
          const newToken = await withRefreshLock(async () => {
            const { data } = await this._apiClient.post('/auth/refresh', undefined, {
              _skipAuthRetry: true,
            })
            return extractAccessToken(data)
          })

          const { useAuthStore } = await import('@/store/useAuthStore')
          // Re-read the profile on every refresh, not only when none is stored: its
          // actions may have changed with a Role edit, which revokes no session.
          // A failed read keeps the stored profile rather than ending the session.
          const previous = useAuthStore.getState().user
          let user = previous
          try {
            const profileRes = await this._apiClient.get('/auth/profile', {
              headers: { Authorization: `Bearer ${newToken}` },
              _skipAuthRetry: true,
            })
            user = profileRes.data.data as User
          } catch (profileError) {
            if (!user) {
              throw profileError
            }
          }
          // Another tab signed out and someone else signed in: this tab's cache and
          // queued requests belong to the previous user, so nothing is replayed under
          // the new identity. The reload starts over as whoever holds the session.
          if (isDifferentIdentity(previous, user)) {
            const changed = new Error('Session changed')
            this._refreshQueue.forEach((cb) => cb.reject(changed))
            this._refreshQueue = []
            globalThis.location.reload()
            return Promise.reject(changed)
          }
          useAuthStore.getState().setAuth(newToken, user)
          this._redirected = false

          this._refreshQueue.forEach((cb) => cb.resolve(newToken))
          this._refreshQueue = []

          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return this._apiClient(originalRequest)
        } catch (refreshError) {
          if (isTransientRefreshError(refreshError)) {
            // Throttled (429) or server hiccup - the refresh token is still
            // good, so fail the pending requests without logging the user out.
            const transientError =
              refreshError instanceof Error ? refreshError : new Error(String(refreshError))
            this._refreshQueue.forEach((cb) => cb.reject(transientError))
            this._refreshQueue = []
            return Promise.reject(transientError)
          }
          const expiredError = new Error('Session expired')
          this._refreshQueue.forEach((cb) => cb.reject(expiredError))
          this._refreshQueue = []
          Session.remove()
          if (!this._redirected) {
            this._redirected = true
            globalThis.location.href = '/'
          }
          return Promise.reject(new Error('Session expired'))
        } finally {
          this._isRefreshing = false
        }
      },
    )
  }

  /**
   * Re-reads the profile and replaces the stored user, so `useHasPermission` follows a
   * Role edit without a reload. One read at a time; a failure keeps the stored profile.
   */
  public syncProfile(): Promise<void> {
    if (this._profileSync) {
      return this._profileSync
    }
    this._profileSync = (async () => {
      try {
        const { useAuthStore } = await import('@/store/useAuthStore')
        const profileRes = await this._apiClient.get('/auth/profile', { _skipAuthRetry: true })
        const { accessToken, setAuth } = useAuthStore.getState()
        if (accessToken) {
          setAuth(accessToken, profileRes.data.data as User)
        }
      } catch {
        // The stored actions stay until the next refresh re-reads them.
      } finally {
        this._profileSync = null
      }
    })()
    return this._profileSync
  }

  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient()
    }
    return ApiClient.instance
  }

  /**
   * Called once at app start. If there is no in-memory access token (e.g. after
   * a page reload), this exchanges the httpOnly session cookie for a new access
   * token so the user stays logged in. The page cannot see the cookie, so it always
   * asks; a 401 just means there is no session.
   */
  public async hydrate(): Promise<void> {
    const { useAuthStore } = await import('@/store/useAuthStore')
    if (Session.isValid()) {
      useAuthStore.getState().setHydrating(false)
      return
    }
    // Transient failures (429 rate limit, 5xx, network) are retried with
    // backoff instead of dropping the session - only a definitive rejection
    // of the refresh token should ever end in the sign-in page.
    const retryDelaysMs = [1_000, 3_000]
    try {
      for (let attempt = 0; ; attempt++) {
        try {
          // Under the same lock as the interceptor: another tab may be rotating
          // the session at the same moment.
          const newToken = await withRefreshLock(async () => {
            const { data } = await this._apiClient.post('/auth/refresh', undefined, {
              _skipAuthRetry: true,
            })
            return extractAccessToken(data)
          })
          const profileRes = await this._apiClient.get('/auth/profile', {
            headers: { Authorization: `Bearer ${newToken}` },
            _skipAuthRetry: true,
          })
          const user = profileRes.data.data as User
          useAuthStore.getState().setAuth(newToken, user)
          useAuthStore.getState().setLanguage(user.language)
          useAuthStore.getState().setTimezone(user.timezone)
          this._redirected = false
          return
        } catch (error) {
          if (!isTransientRefreshError(error) || attempt >= retryDelaysMs.length) {
            throw error
          }
          await delay(retryDelaysMs[attempt])
        }
      }
    } catch (error) {
      // No session cookie, or an ended session: the visitor simply signs in.
      if ((error as AxiosError | undefined)?.response?.status === 401) return
      // biome-ignore lint/suspicious/noConsole: logging hydration errors is important for diagnosing issues
      console.error('Failed to hydrate session', error)
    } finally {
      useAuthStore.getState().setHydrating(false)
    }
  }

  public get(endpoint: string, params?: AxiosRequestConfig<unknown>): Promise<AxiosResponse> {
    return this._apiClient.get(endpoint, { ...params })
  }

  public post(
    endpoint: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    return this._apiClient.post(endpoint, data, config)
  }

  public put(endpoint: string, data: unknown): Promise<AxiosResponse> {
    return this._apiClient.put(endpoint, data)
  }

  public patch(endpoint: string, data: unknown): Promise<AxiosResponse> {
    return this._apiClient.patch(endpoint, data)
  }

  public delete(endpoint: string): Promise<AxiosResponse> {
    return this._apiClient.delete(endpoint)
  }

  public deleteWithBody(endpoint: string, data: unknown): Promise<AxiosResponse> {
    return this._apiClient.delete(endpoint, { data })
  }

  public getBlob(endpoint: string, params?: Record<string, unknown>): Promise<AxiosResponse> {
    return this._apiClient.get(endpoint, {
      params,
      responseType: 'blob',
      paramsSerializer: (p) => {
        if (!p) return ''
        const parts: string[] = []
        for (const [key, value] of Object.entries(p)) {
          if (Array.isArray(value)) {
            for (const item of value) parts.push(`${key}=${encodeURIComponent(item)}`)
          } else if (value !== undefined && value !== null) {
            parts.push(`${key}=${encodeURIComponent(String(value))}`)
          }
        }
        return parts.join('&')
      },
    })
  }
}

export default ApiClient.getInstance()
